import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import Map from 'src/custom_components/Map/Map';
import { useRecieverActions } from 'src/context/providers/RecieversContext';
import CloseIcon from '@mui/icons-material/Close';
import { useCustomerActions } from 'src/context/providers/CustomerContext';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import InputFieds from '../../custom_components/Froms/InputFields';
import { customerAddress, customerCreate } from './view/data';

const CreateRecieverDrawer = ({ isDrawerOpen, handleDrawerToggle }) => {
  const [RecieverData, setRecieverData] = useState({});
  const [userAddressInfo, setUserAddressInfo] = useState({});

  const { getPhysicalCustomers } = useCustomerActions();

  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [CustomerOptions, setCustomerOptions] = useState([]);
  const [Customer, setCustomere] = useState({
    id: '',
    name: '',
  });

  const fetchCustomeres = useCallback(
    async (filters) => {
      setLoadingCustomer(true);
      try {
        const response = await getPhysicalCustomers(filters);
        setCustomerOptions(response?.customers);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingCustomer(false);
      }
    },
    [getPhysicalCustomers]
  );

  useEffect(() => {
    const initialFilters = {
      take: 5,
    };
    fetchCustomeres(initialFilters);
  }, [fetchCustomeres]);
  // Fetch Customer data based on input

  const handleCustomerInputChange = debounce((event, newInputValue) => {
    const filters = {
      name: newInputValue,
      take: 5,
    };
    fetchCustomeres(filters);
  }, 500);

  const { createReciever } = useRecieverActions();

  const Clear = () => {
    setRecieverData({});
    setUserAddressInfo({});
  };

  const handleCreateReciever = async () => {
    // REQUIRED FIELDS (no billing fields here)
    if (
      !RecieverData.first_name ||
      !RecieverData.last_name ||
      !RecieverData.email ||
      !RecieverData.phone ||
      !userAddressInfo.address1 ||
      !userAddressInfo.city
    ) {
      toast.error('Please fill in all required fields');
      return;
    }

    const customerData = {
      first_name: RecieverData.first_name,
      last_name: RecieverData.last_name,
      id_number: RecieverData.id_number,
      email: RecieverData.email,
      phone: RecieverData.phone,
      phone_secondary: RecieverData.phone_secondary,
      marketing_permissions: true,
      isPrimary: !!RecieverData.isPrimary,
      customer_id: Customer.id,
    };

    await createReciever(customerData, userAddressInfo);
    Clear();
    handleDrawerToggle();
  };

  const DrawerConfig = {
    SubNavigation: true,
    anchor: 'right',
    width: '40%',
    drawerHeaderText: 'Create Receiver',
    buttons: {
      status: true,
      elements: [{ name: 'Create', onClick: handleCreateReciever }],
    },
  };

  const containerStyle = {
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    position: 'fixed',
    zIndex: 2,
    top: 0,
    left: 0,
    display: isDrawerOpen ? 'block' : 'none',
    cursor: 'pointer',
  };

  const handleOutsideClick = (event) => {
    if (event.target === event.currentTarget) {
      handleDrawerToggle();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      style={containerStyle}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          handleDrawerToggle();
        }
      }}
      onClick={handleOutsideClick}
    >
      <Drawer
        variant="persistent"
        anchor={DrawerConfig.anchor}
        open={isDrawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: { width: { lg: DrawerConfig.width, xs: '100%', md: '100%' } },
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderBottom: '1px solid rgba(220, 220, 220, 0.2)',
          }}
        >
          <Typography variant="h6">{DrawerConfig.drawerHeaderText}</Typography>
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Form */}
        <div
          className="content-wrap"
          style={{
            gap: '2px',
            padding: '0px 0px 0px 0px',
            alignItems: 'center',
            justifyContent: 'space-evenly',
          }}
        >
          {/* User Info */}
          <InputFieds
            userData={RecieverData}
            heading="User Information"
            inputFields={customerCreate}
            setUserData={setRecieverData}
          />

          {/* Primary checkbox (NOT used for address copying anymore) */}
          <div style={{ margin: '0px auto', width: '90%' }}>
            <FormControlLabel
              control={
                <Checkbox
                  id="primaryReceiverCheckbox"
                  checked={!!RecieverData.isPrimary}
                  onChange={(e) =>
                    setRecieverData((prev) => ({
                      ...prev,
                      isPrimary: e.target.checked,
                    }))
                  }
                />
              }
              label="Primary"
            />
          </div>

          <Autocomplete
            sx={{ width: '96%', margin: '10px auto' }}
            options={CustomerOptions ?? []}
            loading={loadingCustomer}
            // What to show in the input after selection
            getOptionLabel={(option) => {
              const first = option?.name || option?.first_name || '';
              const last = option?.lname || option?.last_name || '';
              const idn = option?.id_number ? ` • ${option.id_number}` : '';
              return `${first} ${last}${idn}`.trim();
            }}
            isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
            onChange={(event, newValue) => setCustomere(newValue)}
            onInputChange={(event, newInputValue) =>
              handleCustomerInputChange(event, newInputValue)
            }
            renderOption={(props, option) => {
              const first = option?.name || option?.first_name || '';
              const last = option?.lname || option?.last_name || '';
              const idn = option?.id_number || '';
              const email = option?.email || '';
              return (
                <li {...props} key={option?.id ?? `${first}-${last}-${idn}`}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600 }}>
                      {first} {last} {idn && <span style={{ color: '#637381' }}>• {idn}</span>}
                    </span>
                    {email && <span style={{ fontSize: 12, color: '#637381' }}>{email}</span>}
                  </div>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customers"
                variant="filled"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCustomer ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Map
            initialLocation={undefined}
            onLocationChange={(loc) => {
              setUserAddressInfo((prev) => ({
                ...prev,
                lat: loc.lat,
                long: loc.long,
                city: loc.city,
                zipCode: loc.zipCode,
                country: loc.country,
                address1: loc.address1,
              }));
            }}
          />

          {/* Shipping Address ONLY (no billing fields) */}
          <div style={{ marginTop: '20px' }}>
            <InputFieds
              userData={userAddressInfo}
              heading=""
              inputFields={customerAddress}
              setUserData={setUserAddressInfo}
            />
          </div>
        </div>

        {/* Buttons */}
        {DrawerConfig.buttons && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              borderTop: '1px solid rgba(220, 220, 220, 0.2)',
              padding: '16px',
              bottom: '0',
              left: '0',
              width: '100%',
            }}
          >
            {DrawerConfig.buttons.elements.map((element, index) => (
              <Button
                variant="contained"
                key={index}
                style={{ marginLeft: '24px', width: '136px' }}
                onClick={element.onClick}
              >
                Create
              </Button>
            ))}
          </div>
        )}
      </Drawer>
    </div>
  );
};

CreateRecieverDrawer.propTypes = {
  isDrawerOpen: PropTypes.bool.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
};

export default CreateRecieverDrawer;
